import {
  adminExerciseRequestDetailSchema,
  adminExerciseRequestSchema,
  type AdminExerciseRequest,
  type AdminExerciseRequestDetail,
  type AdminExerciseRequestRejectRequest,
  type AdminExerciseRequestResolveRequest,
  type ExerciseRequestSort,
  type ExerciseRequestStatusName,
} from '@steado/admin-contract/exercise-requests';
import { paginationMetaSchema } from '@steado/admin-contract/pagination';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient } from '@/lib/api/client';

import type { ExerciseRequestsSearch } from './exercise-requests-search';

export const EXERCISE_REQUESTS_PAGE_SIZE = 20;

/** While a draft is being written, the drawer asks again this often. */
export const DRAFT_POLL_MS = 4_000;

const exerciseRequestsPageSchema = z.object({
  items: z.array(adminExerciseRequestSchema),
  pagination: paginationMetaSchema,
});
export type ExerciseRequestsPage = z.infer<typeof exerciseRequestsPageSchema>;

/** The status the list asks for: the open queue unless the URL says otherwise. */
export function statusOf(search: ExerciseRequestsSearch): ExerciseRequestStatusName | 'all' {
  return search.status ?? 'open';
}

export function sortOf(search: ExerciseRequestsSearch): ExerciseRequestSort {
  return search.sort ?? 'reach';
}

export function exerciseRequestsQueryOptions(api: ApiClient, search: ExerciseRequestsSearch) {
  const status = statusOf(search);
  const query = {
    status: status === 'all' ? undefined : status,
    sort: sortOf(search),
    cursor: search.cursor,
    limit: EXERCISE_REQUESTS_PAGE_SIZE,
  };
  return queryOptions({
    queryKey: ['admin', 'exercise-requests', query],
    queryFn: async (): Promise<ExerciseRequestsPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/exercise-requests',
        query,
        schema: z.array(adminExerciseRequestSchema),
      });
      return exerciseRequestsPageSchema.parse({
        items: response.data,
        pagination: response.meta.pagination,
      });
    },
    placeholderData: (previous) => previous,
  });
}

export function exerciseRequestQueryOptions(api: ApiClient, id: string | null) {
  return queryOptions({
    queryKey: ['admin', 'exercise-request', id],
    queryFn: (): Promise<AdminExerciseRequestDetail> =>
      api.request({ path: `/admin/exercise-requests/${id ?? ''}`, schema: adminExerciseRequestDetailSchema }),
    enabled: id !== null,
    // Polls only while a draft is queued, so an open drawer shows it land.
    refetchInterval: (query) => (query.state.data?.draftStatus === 'queued' ? DRAFT_POLL_MS : false),
  });
}

export function draftExerciseRequest(api: ApiClient, id: string): Promise<AdminExerciseRequest> {
  return api.request({
    path: `/admin/exercise-requests/${id}/draft`,
    method: 'POST',
    schema: adminExerciseRequestSchema,
  });
}

export function resolveExerciseRequest(
  api: ApiClient,
  id: string,
  body: AdminExerciseRequestResolveRequest,
): Promise<AdminExerciseRequest> {
  return api.request({
    path: `/admin/exercise-requests/${id}/resolve`,
    method: 'POST',
    body,
    schema: adminExerciseRequestSchema,
  });
}

export function rejectExerciseRequest(
  api: ApiClient,
  id: string,
  body: AdminExerciseRequestRejectRequest,
): Promise<AdminExerciseRequest> {
  return api.request({
    path: `/admin/exercise-requests/${id}/reject`,
    method: 'POST',
    body,
    schema: adminExerciseRequestSchema,
  });
}

/** One sample's photo. Admin only and audited on the server: fetched on a click, never prefetched. */
export function fetchSamplePhoto(api: ApiClient, id: string, scanId: string): Promise<Blob> {
  return api.requestBlob({ path: `/admin/exercise-requests/${id}/samples/${scanId}/photo` });
}
